import React from "react";
import { Star, MessagesSquare, Users, Sparkles, ArrowUp, Globe, Plus } from "lucide-react";

const features = [
  {
    title: "Authenticity",
    description: "Our platform promotes genuine reviews that build trust between brands and audiences.",
    icon: Star,
  },
  {
    title: "Transparency",
    description: "Clear campaign requirements, fair compensation, and open communication channels.",
    icon: MessagesSquare,
  },
  {
    title: "Growth",
    description: "Tools and opportunities designed to help both influencers and brands expand their reach.",
    icon: Users,
  },
];

const stats = [
  { value: "100+", emoji: "🤝", label: "Brand Partners" },
  { value: "500+", emoji: "⭐", label: "Reviews Published" },
  { value: "24/7", emoji: "🛠️", label: "Platform Support" },
];

export default function WhyChooseUs() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center mb-12">
          <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Why Choose Us</h2>
          <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            The Platform That Puts You First
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            We've built Engaged Manila with a focus on transparency, authenticity, and mutual success.
          </p>
        </div>
        <div className="mx-auto w-full max-w-3xl space-y-12">
          {/* Large bento card with illustration */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm p-0">
            {/* Background gradient */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, #eff6ff 0%, #f5f3ff 50%, #fefce8 100%)",
              }}
            />
            <div className="relative m-auto max-w-md p-6 sm:p-12">
              <PlatformIllustration />
            </div>
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 @2xl:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-500">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const PlatformIllustration = () => {
  return (
    <div className="relative space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="w-fit">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 fill-[#FFC107] stroke-[#FFC107]" />
          <span className="text-xs font-medium text-gray-400">Engaged Manila</span>
        </div>
        <p className="mt-2 line-clamp-2 text-sm text-gray-700">
          How can I find the right influencers for my brand's product launch?
        </p>
        <ul role="list" className="mt-3 space-y-2 text-sm text-gray-500">
          {stats.map((stat, index) => (
            <li key={index} className="-ml-0.5 flex items-center gap-2">
              <span>{stat.emoji}</span>
              <span className="font-medium text-gray-900">{stat.value}</span>{" "}
              {stat.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="-mx-3 -mb-3 space-y-3 rounded-lg bg-gray-50 p-3">
        <div className="text-sm text-gray-400">Find your match</div>
        <div className="flex justify-between">
          <div className="flex gap-2">
            <button className="flex items-center justify-center w-7 h-7 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <Plus className="w-3.5 h-3.5 text-gray-500" />
            </button>
            <button className="flex items-center justify-center w-7 h-7 rounded-2xl border border-gray-200 bg-white shadow-sm">
              <Globe className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
          <button className="flex items-center justify-center w-7 h-7 rounded-2xl bg-primary shadow-sm">
            <ArrowUp strokeWidth={3} className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};
